import { Component, Input, OnDestroy, OnInit, Type } from '@angular/core';
import { Validators } from '@angular/forms';

import { DialogService } from 'primeng/dynamicdialog';

import { filter, Subject, takeUntil } from 'rxjs';

import { IPaperSupplementsData } from 'editing-tool/src/app/shared/components/paper/steps/supplements/paper-supplements-data';
import { PaperSupplementsRenderers } from 'editing-tool/src/app/shared/components/paper/steps/supplements/paper-supplements-renderers';
import { ISupplementFileDialogData } from 'editing-tool/src/app/shared/components/paper/steps/supplements/supplement-file-dialog/supplement-file-dialog-dialog-data';
import { SupplementFileDialogComponent } from 'editing-tool/src/app/shared/components/paper/steps/supplements/supplement-file-dialog/supplement-file-dialog.component';
import { ISupplementLinkDialogData } from 'editing-tool/src/app/shared/components/paper/steps/supplements/supplement-link-dialog/supplement-link-dialog-data';
import { SupplementLinkDialogComponent } from 'editing-tool/src/app/shared/components/paper/steps/supplements/supplement-link-dialog/supplement-link-dialog.component';
import { Renderers, ScConfirmationService, TableSettings, ValidationConstants, ValidationService } from 'sc-common';
import { ApiPaperSupplementListItem } from 'sc-common/core/services/open-api/main-api-client';

@Component({
    selector: 'et-paper-supplements',
    templateUrl: './paper-supplements.component.html',
    standalone: false
})
export class PaperSupplementsComponent implements OnInit, OnDestroy {

    @Input()
    public data: IPaperSupplementsData;

    @Input()
    public isReadonly: boolean;

    public tableSettings: TableSettings<ApiPaperSupplementListItem>;

    private readonly _destroy$ = new Subject<void>();

    private readonly _refreshTable$ = new Subject<void>();

    constructor(
        private readonly _dialogService: DialogService,
        private readonly _confirmationService: ScConfirmationService) {
    }

    public ngOnInit(): void {
        this._initTable();
    }

    public ngOnDestroy(): void {
        this._refreshTable$.complete();
        this._destroy$.next();
        this._destroy$.complete();
    }

    public addLink(): void {
        const dialogData: ISupplementLinkDialogData = {
            action: model => this.data.addSupplementLink(model)
        };

        this._openDialog(SupplementLinkDialogComponent, $localize`Add link`, dialogData);
    }

    public addFile(): void {
        const dialogData: ISupplementFileDialogData = {
            action: model => this.data.addSupplementFile(model),
            getUploadUrl: supplementId => this.data.getUploadSupplementFileUrl(supplementId),
            validationRules$: this.data.validationRules$
        };

        this._openDialog(SupplementFileDialogComponent, $localize`Add file`, dialogData);
    }

    public onDeleteSupplement(item: ApiPaperSupplementListItem): void {
        this._confirmationService.confirm({
            title: $localize`Delete Supplement`,
            message: $localize`Are you sure you want to delete this Supplement?`,
            acceptLabel: $localize`Yes`,
            rejectLabel: $localize`No`,
            accept$: this.data.removeSupplement(item.id),
            acceptCallback: () => this._refreshTable$.next()
        });
    }

    private _openDialog(componentType: Type<any>, header: string, dialogData: any): void {

        const dialogRef = this._dialogService.open(componentType, {
            width: '30vw',
            header: header,
            closable: true,
            data: dialogData
        });

        dialogRef.onClose
            .pipe(filter(response => response), takeUntil(this._destroy$))
            .subscribe(() => this._refreshTable$.next());
    }

    private _initTable(): void {

        this.tableSettings = TableSettings.create(ApiPaperSupplementListItem, {
            dataSource: oDataQuery => this.data.getList(oDataQuery),
            defaultSort: {
                field: m => m.id,
                order: 'asc'
            },
            columnResizeMode: 'fit',
            paginator: false,
            rowHover: false,
            hasFilters: false,
            reload$: this._refreshTable$.asObservable(),
            columns: {
                materials: {
                    renderer: PaperSupplementsRenderers.materialsRenderer(
                        (item: ApiPaperSupplementListItem) => this.data.getFile(item.id)),
                    sortable: false,
                    filterable: false
                },
                description: {
                    editable: this.isReadonly ?
                        null :
                        {
                            callback: (_, item) => this.data.updateSupplement(item.id, item),
                            validators: [Validators.required, ValidationService.alphaNumSymbolValidator, Validators.maxLength(ValidationConstants.Max500Length)],
                            showIcon: true,
                            showValidationTooltip: true
                        },
                    renderer: Renderers.rawHtml(),
                    sortable: false,
                    filterable: false
                },
                uploadedBy: {
                    hidden: true
                },
                added: {
                    hidden: true
                }
            }
        });
    }
}
